import { createRouter, createWebHistory } from 'vue-router';
import { useUserStore } from '@/store/modules/user';
import { asyncRoutes } from './routes';
import { filterRoutesByPermission, extractPermissionKeys } from '@sys/shared/permission';
import {
  generateRoutesFromRoutePages,
  addDynamicRoutesToLayout,
} from './resolveRoutePageComponent';

// 静态路由（无 permission 字段视为公开页面）
const constantRoutes = [
  {
    path: '/login',
    name: 'Login',
    component: () => import('@/views/login/index.vue'),
    meta: { hidden: true },
  },
  {
    path: '/',
    name: 'Layout',
    component: () => import('@/layout/DefaultLayout.vue'),
    children: [],
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'NotFound',
    component: () => import('@/views/error/404.vue'),
    meta: { hidden: true },
  },
];

const router = createRouter({
  history: createWebHistory('/brand-admin/'),
  routes: constantRoutes,
});

// 标记是否已挂载动态路由
let dynamicRoutesAdded = false;
let dynamicHomePath = null;

/**
 * 获取本地路由树中的第一个可访问页面路径。
 * 分组路由只作为容器，优先进入其第一个可见叶子页面。
 */
function getFirstNavigablePath(routes, parentPath = '') {
  for (const route of routes) {
    const routePath = route.path?.startsWith('/')
      ? route.path
      : `${parentPath}/${route.path || ''}`.replace(/\/+/g, '/');

    if (Array.isArray(route.children)) {
      const childPath = getFirstNavigablePath(route.children, routePath);
      if (childPath) return childPath;
      continue;
    }

    if (!route.meta?.hidden) return routePath;
  }

  return null;
}

/**
 * 从后端左侧菜单树中获取第一个已成功注册的叶子页面。
 * menu_routes 不包含新增、编辑、详情等隐藏路由，适合作为登录首页来源。
 */
function getFirstMenuPagePath(menuRoutes) {
  if (!Array.isArray(menuRoutes)) return null;

  for (const item of menuRoutes) {
    if (item.children?.length) {
      const childPath = getFirstMenuPagePath(item.children);
      if (childPath) return childPath;
      continue;
    }

    if (!item.path) continue;
    const path = item.path.startsWith('/') ? item.path : `/${item.path}`;
    if (router.resolve(path).name !== 'NotFound') return path;
  }

  return null;
}

/**
 * 加载并挂载动态路由
 * @param {string[]} permissions 用户权限列表
 */
function loadDynamicRoutes(permissions) {
  const layout = router.getRoutes().find(r => r.name === 'Layout');
  if (!layout) return null;

  const userStore = useUserStore();
  const hasBackend = userStore.routePages?.length > 0;

  // 清空之前添加的子路由（热更新场景）
  layout.children
    ?.filter(child => child.name !== 'Dashboard')
    ?.forEach(child => {
      router.removeRoute(child.name);
    });

  if (hasBackend) {
    // 后端驱动：从 route_pages 动态解析组件并注册
    const routes = generateRoutesFromRoutePages(userStore.routePages);
    addDynamicRoutesToLayout(router, routes);
    dynamicHomePath = getFirstMenuPagePath(userStore.menuRoutes);
  } else {
    // 本地 fallback
    const filtered = filterRoutesByPermission(asyncRoutes, permissions);
    filtered.forEach(route => {
      router.addRoute('Layout', route);
    });
    dynamicHomePath = getFirstNavigablePath(filtered);
  }

  dynamicRoutesAdded = true;
  return dynamicHomePath;
}

/**
 * 重置动态路由（用于登出或切换账号）
 */
export function resetRouter() {
  const layout = router.getRoutes().find(r => r.name === 'Layout');
  if (!layout) return;

  layout.children?.forEach(child => {
    router.removeRoute(child.name);
  });

  dynamicRoutesAdded = false;
  dynamicHomePath = null;
}

// ==================== 路由守卫 ====================
// 规则：无 permission 字段的路由直接放行；有 permission 则匹配用户权限 key
// 预留区域：可根据业务需要手动补充守卫规则

router.beforeEach(async (to, from, next) => {
  const userStore = useUserStore();

  // 1. 已登录但未挂载动态路由 -> 先挂载，再重新导航
  // 必须放在最前面，否则 redirect 到子路由时会因路由不存在而匹配 catch-all 404
  if (userStore.isLoggedIn && !dynamicRoutesAdded) {
    const permissions = userStore.permissions?.length
      ? userStore.permissions
      : extractAllPermissions();
    const homePath = loadDynamicRoutes(permissions);
    if ((to.path === '/' || to.path === '') && homePath) {
      return next({ path: homePath, replace: true });
    }
    return next({ path: to.path, query: to.query, hash: to.hash, replace: true });
  }

  // 2. 未登录 -> 只有 Login 页能访问，其余全部重定向到登录
  // 命中 NotFound 时不保存重定向路径，避免登录后跳转到不存在的页面
  if (!userStore.isLoggedIn) {
    if (to.name === 'Login') {
      return next();
    }
    if (to.name === 'NotFound') {
      return next({ name: 'Login' });
    }
    return next({ name: 'Login', query: { redirect: to.fullPath } });
  }

  // 3. 已登录访问登录页 -> 跳转首页
  if (to.name === 'Login') {
    return next(dynamicHomePath || '/');
  }

  // 4. 访问根路径时进入当前用户实际拥有的第一条业务路由
  if ((to.path === '/' || to.path === '') && dynamicHomePath) {
    return next(dynamicHomePath);
  }

  // 5. 无 permission 标识的路由视为公开页面，直接放行
  if (!to.meta?.permission) {
    return next();
  }

  // 6. 权限 key 匹配校验
  if (!userStore.hasPermission(to.meta.permission)) {
    return next({ name: 'NotFound' });
  }

  // 7. 自定义守卫规则预留区（手动填写）
  // --------------------------------------------------
  // 示例：
  // if (to.meta?.needBrandNo && !userStore.brandNo) {
  //   return next('/select-brand')
  // }
  // --------------------------------------------------

  next();
});

router.afterEach(to => {
  document.title = to.meta?.title ? `${to.meta.title} - 品牌后台` : '品牌后台';
});

/**
 * 提取本地所有权限 key（仅本地开发兜底用）
 */
function extractAllPermissions() {
  return extractPermissionKeys(asyncRoutes);
}

export default router;
